<script setup>
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import MediaLibraryAPI from 'dashboard/api/mediaLibrary';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPreview from 'dashboard/components-next/media-library/MediaLibraryPreview.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  acceptedTypes: { type: String, default: '' },
});
const emit = defineEmits(['select']);
const { t } = useI18n();
const { accountId } = useAccount();
const dialog = ref(null);
const visible = ref(false);
const items = ref([]);
const query = ref('');
const page = ref(1);
const total = ref(0);
const busy = ref(false);
const error = ref('');
let revision = 0;
let session = 0;
const browseBy = ref('files');
const filter = ref('');
const taxonomy = ref({ products: [], categories: [], tags: [] });
const taxonomyError = ref('');
const taxonomyBusy = ref(false);
const modes = computed(() => [
  {
    value: 'files',
    label: t('MEDIA_LIBRARY.ALL_FILES'),
    icon: 'i-lucide-files',
  },
  {
    value: 'products',
    label: t('MEDIA_LIBRARY.TAXONOMY.products'),
    icon: 'i-lucide-package',
  },
  {
    value: 'categories',
    label: t('MEDIA_LIBRARY.TAXONOMY.categories'),
    icon: 'i-lucide-folder',
  },
  { value: 'tags', label: t('MEDIA_LIBRARY.TAGS'), icon: 'i-lucide-tags' },
]);
const filterLabel = computed(
  () => modes.value.find(mode => mode.value === browseBy.value).label
);
const filterOptions = computed(() => [
  { value: '', label: t('MEDIA_LIBRARY.ALL_FILES') },
  ...(taxonomy.value[browseBy.value] || []).map(record => ({
    value: record.id,
    label: record.name,
  })),
]);
const filtered = computed(() => Boolean(query.value.trim() || filter.value));
async function loadTaxonomy() {
  const currentSession = session;
  taxonomyBusy.value = true;
  taxonomyError.value = '';
  try {
    const { data } = await MediaLibraryAPI.taxonomy({ published_only: true });
    if (currentSession === session) taxonomy.value = data;
  } catch {
    if (currentSession === session)
      taxonomyError.value = t('MEDIA_LIBRARY.FILTER_ERROR');
  } finally {
    if (currentSession === session) taxonomyBusy.value = false;
  }
}
const accepted = item =>
  props.acceptedTypes.split(',').some(value => {
    const type = value.trim().toLowerCase();
    return (
      type === '*/*' ||
      type === item.content_type?.toLowerCase() ||
      (type.startsWith('.') && item.filename.toLowerCase().endsWith(type)) ||
      (type.endsWith('/*') && item.content_type?.startsWith(type.slice(0, -1)))
    );
  });
const load = async (nextPage = 1) => {
  revision += 1;
  const request = revision;
  busy.value = true;
  error.value = '';
  items.value = [];
  try {
    const { data } = await MediaLibraryAPI.search({
      q: query.value,
      page: nextPage,
      published_only: true,
      product_id:
        browseBy.value === 'products' ? filter.value || undefined : undefined,
      category_id:
        browseBy.value === 'categories' ? filter.value || undefined : undefined,
      tag: browseBy.value === 'tags' ? filter.value || undefined : undefined,
    });
    if (request !== revision) return;
    items.value = data.items;
    total.value = data.total;
    page.value = nextPage;
  } catch {
    if (request === revision) error.value = t('MEDIA_LIBRARY.ERROR');
  } finally {
    if (request === revision) busy.value = false;
  }
};
function changeMode(value) {
  browseBy.value = value;
  filter.value = '';
  load();
}
function changeFilter(value) {
  filter.value = value;
  load();
}
function clearFilters() {
  query.value = '';
  filter.value = '';
  load();
}

const close = () => {
  visible.value = false;
  session += 1;
  revision += 1;
  items.value = [];
  busy.value = false;
};
const select = async item => {
  if (busy.value || !accepted(item)) return;
  const request = revision;
  busy.value = true;
  try {
    const { data } = await MediaLibraryAPI.downloadPublished(item.id);
    if (request !== revision) return;
    emit(
      'select',
      new File([data], item.filename, { type: item.content_type })
    );
    close();
  } catch {
    if (request === revision) error.value = t('MEDIA_LIBRARY.ERROR');
  } finally {
    if (request === revision) busy.value = false;
  }
};
onBeforeUnmount(close);
watch(
  () => [accountId.value, props.conversationId],
  () => {
    close();
  }
);
defineExpose({
  async open() {
    visible.value = true;
    await nextTick();
    query.value = '';
    browseBy.value = 'files';
    filter.value = '';
    taxonomy.value = { products: [], categories: [], tags: [] };
    taxonomyError.value = '';
    dialog.value.open();
    load();
    loadTaxonomy();
  },
});
</script>

<template>
  <Dialog
    v-if="visible"
    ref="dialog"
    :title="t('MEDIA_LIBRARY.TITLE')"
    :description="t('MEDIA_LIBRARY.PICK_HELP')"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="t('MEDIA_LIBRARY.CANCEL')"
    @close="close"
  >
    <div
      class="flex flex-wrap gap-1"
      role="group"
      :aria-label="t('MEDIA_LIBRARY.BROWSE_BY')"
    >
      <Button
        v-for="mode in modes"
        :key="mode.value"
        :label="mode.label"
        :icon="mode.icon"
        :aria-pressed="browseBy === mode.value"
        :variant="browseBy === mode.value ? 'faded' : 'ghost'"
        color="slate"
        :disabled="busy"
        @click="changeMode(mode.value)"
      />
    </div>
    <ComboBox
      v-if="browseBy !== 'files'"
      :key="browseBy"
      :model-value="filter"
      :options="filterOptions"
      :aria-label="filterLabel"
      :search-placeholder="t('MEDIA_LIBRARY.SEARCH')"
      :disabled="busy || taxonomyBusy"
      @update:model-value="changeFilter"
    />
    <div v-if="taxonomyError" class="flex items-center gap-2">
      <p role="alert" class="m-0 text-sm text-n-ruby-11">{{ taxonomyError }}</p>
      <Button
        :label="t('MEDIA_LIBRARY.RETRY')"
        variant="outline"
        :disabled="taxonomyBusy"
        @click="loadTaxonomy"
      />
    </div>
    <div class="flex items-center gap-2" role="search">
      <input
        v-model="query"
        type="search"
        class="!mb-0 min-w-0 flex-1"
        :aria-label="t('MEDIA_LIBRARY.SEARCH')"
        :placeholder="t('MEDIA_LIBRARY.SEARCH')"
        @keydown.enter.prevent="!busy && load()"
      />
      <Button
        type="button"
        :label="t('MEDIA_LIBRARY.SEARCH_ACTION')"
        class="!bg-n-blue-11 dark:!text-n-slate-1 hover:enabled:!brightness-90 focus-visible:!brightness-90 focus-visible:!outline-n-blue-11 focus-visible:outline-offset-2"
        :disabled="busy"
        @click="load()"
      />
    </div>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="busy" role="status" class="text-sm text-n-slate-11">
      {{ t('MEDIA_LIBRARY.LOADING') }}
    </p>
    <ul class="m-0 max-h-80 list-none space-y-2 overflow-y-auto p-0">
      <li
        v-for="item in items"
        :key="item.id"
        class="flex items-center justify-between gap-3 rounded-lg border border-n-weak p-3"
      >
        <MediaLibraryPreview
          :item="item"
          published-only
          class="size-14 shrink-0"
        />
        <div class="min-w-0 flex-1">
          <p class="m-0 truncate font-medium">{{ item.title }}</p>
          <p class="m-0 break-all text-xs text-n-slate-11">
            {{ item.filename }}
          </p>
          <p
            v-if="item.product || item.photo_kind"
            class="m-0 break-words text-xs text-n-slate-11"
          >
            {{
              [item.category_name, item.product?.name, item.photo_kind]
                .filter(Boolean)
                .join(' · ')
            }}
          </p>
          <p
            v-if="item.tags?.length"
            class="m-0 break-words text-xs text-n-slate-11"
          >
            {{ item.tags.join(' · ') }}
          </p>
          <p v-if="!accepted(item)" class="m-0 text-xs text-n-slate-11">
            {{ t('MEDIA_LIBRARY.UNSUPPORTED') }}
          </p>
        </div>
        <Button
          :label="t('MEDIA_LIBRARY.ATTACH')"
          class="!bg-n-blue-11 dark:!text-n-slate-1 hover:enabled:!brightness-90 focus-visible:!brightness-90 focus-visible:!outline-n-blue-11 focus-visible:outline-offset-2"
          icon="i-lucide-paperclip"
          :disabled="busy || !accepted(item)"
          @click="select(item)"
        />
      </li>
    </ul>
    <div
      v-if="!busy && !error && !items.length"
      class="flex flex-col items-start gap-2 rounded-lg bg-n-alpha-1 p-4"
    >
      <p class="m-0 font-medium">{{ t('MEDIA_LIBRARY.EMPTY') }}</p>
      <p class="m-0 text-sm text-n-slate-11">
        {{
          filtered
            ? t('MEDIA_LIBRARY.PICK_EMPTY_FILTERED')
            : t('MEDIA_LIBRARY.PICK_EMPTY_UNPUBLISHED')
        }}
      </p>
      <Button
        v-if="filtered"
        :label="t('MEDIA_LIBRARY.CLEAR_FILTERS')"
        variant="outline"
        @click="clearFilters"
      />
    </div>
    <div v-if="total > 30" class="flex gap-2">
      <Button
        :label="t('MEDIA_LIBRARY.PREVIOUS')"
        class="!bg-n-blue-11 dark:!text-n-slate-1 hover:enabled:!brightness-90 focus-visible:!brightness-90 focus-visible:!outline-n-blue-11 focus-visible:outline-offset-2"
        :disabled="busy || page === 1"
        @click="load(page - 1)"
      />
      <Button
        :label="t('MEDIA_LIBRARY.NEXT')"
        class="!bg-n-blue-11 dark:!text-n-slate-1 hover:enabled:!brightness-90 focus-visible:!brightness-90 focus-visible:!outline-n-blue-11 focus-visible:outline-offset-2"
        :disabled="busy || page * 30 >= total"
        @click="load(page + 1)"
      />
    </div>
  </Dialog>
</template>
